/**
 * Gerenciador global de sons de alarme.
 *
 * - Config persistida em localStorage (chave "oltm_alarm_sounds_v1").
 * - Sons armazenados como data URL (base64) — sem endpoint no backend ainda.
 * - Exposto via `playAlarmSound(category)` para uso em Dashboard, listeners globais,
 *   toasts, etc.
 */

const STORAGE_KEY = 'oltm_alarm_sounds_v1';

// Categorias suportadas (severidade + tipos de evento).
export const ALARM_CATEGORIES = [
  { key: 'critical', label: 'Crítico', kind: 'severity', color: '#dc2626' },
  { key: 'major', label: 'Major', kind: 'severity', color: '#ea580c' },
  { key: 'minor', label: 'Minor', kind: 'severity', color: '#f59e0b' },
  { key: 'warning', label: 'Warning', kind: 'severity', color: '#eab308' },
  { key: 'info', label: 'Info', kind: 'severity', color: '#0ea5e9' },
  { key: 'onu_offline', label: 'ONU Offline', kind: 'event', color: '#525252' },
  { key: 'power_fail', label: 'Power Fail (Dying Gasp)', kind: 'event', color: '#7c2d12' },
  { key: 'los', label: 'Loss of Signal (LOS)', kind: 'event', color: '#b91c1c' },
  { key: 'rx_low', label: 'Rx Baixo (alerta)', kind: 'event', color: '#f97316' },
  { key: 'rx_critical', label: 'Rx Crítico', kind: 'event', color: '#dc2626' },
  { key: 'new_pending_onu', label: 'Nova ONU pendente', kind: 'event', color: '#2563eb' },
  { key: 'olt_offline', label: 'OLT Offline', kind: 'event', color: '#450a0a' },
];

export const CATEGORY_KEYS = ALARM_CATEGORIES.map((c) => c.key);

// Estado inicial (sem uploads, tudo habilitado, som global ligado, volume 0.7)
function defaultSettings() {
  const sounds = {};
  CATEGORY_KEYS.forEach((k) => {
    sounds[k] = { enabled: true, dataUrl: null, name: '' };
  });
  return {
    globalEnabled: true,
    globalWatcher: true, // Toca também fora do dashboard (watcher em qualquer tela)
    volume: 0.7,
    sounds,
  };
}

export function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSettings();
    const parsed = JSON.parse(raw);
    // merge com defaults (novas categorias adicionadas em versões futuras).
    const base = defaultSettings();
    return {
      ...base,
      ...parsed,
      sounds: { ...base.sounds, ...(parsed.sounds || {}) },
    };
  } catch (e) {
    return defaultSettings();
  }
}

export function saveSettings(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    // Notifica outras abas / componentes.
    try { window.dispatchEvent(new CustomEvent('oltm-alarm-sounds-changed', { detail: next })); } catch (e) { /* noop */ }
  } catch (e) { /* quota / privacidade */ }
}

/**
 * Toca o som configurado para a categoria. Aceita sinônimos ("high" → "critical" etc.).
 * Retorna true se conseguiu iniciar a reprodução.
 */
export function playAlarmSound(rawCategory) {
  const settings = loadSettings();
  if (!settings.globalEnabled) return false;
  const category = normalizeCategory(rawCategory);
  const cfg = settings.sounds[category];
  if (!cfg || !cfg.enabled) return false;
  if (!cfg.dataUrl) {
    // Sem MP3 carregado → toca beep sintetizado como fallback.
    return synthBeep(category, settings.volume);
  }
  try {
    const audio = new Audio(cfg.dataUrl);
    audio.volume = Math.max(0, Math.min(1, settings.volume ?? 0.7));
    audio.play().catch(() => { /* autoplay bloqueado antes de gesto do usuário */ });
    return true;
  } catch (e) {
    return false;
  }
}

// Mapeia strings comuns de severidade / tipo de evento vindas da API
export function normalizeCategory(raw) {
  if (!raw) return 'info';
  const s = String(raw).toLowerCase().trim();
  if (['critical', 'crit', 'severe', 'high', 'urgent'].includes(s)) return 'critical';
  if (['major', 'error', 'err'].includes(s)) return 'major';
  if (['minor'].includes(s)) return 'minor';
  if (['warning', 'warn', 'medium', 'med'].includes(s)) return 'warning';
  if (['info', 'informational', 'low', 'notice'].includes(s)) return 'info';
  if (CATEGORY_KEYS.includes(s)) return s;
  // heurísticas por trecho no rótulo
  if (s.includes('dying') || s.includes('pwr') || s.includes('power')) return 'power_fail';
  if (s.includes('los') || s.includes('loss')) return 'los';
  if (s.includes('offline') && s.includes('olt')) return 'olt_offline';
  if (s.includes('offline')) return 'onu_offline';
  if (s.includes('pending') || s.includes('autoriz')) return 'new_pending_onu';
  if (s.includes('rx') && (s.includes('crit') || s.includes('severe'))) return 'rx_critical';
  if (s.includes('rx') || s.includes('signal')) return 'rx_low';
  return 'info';
}

/** Beep sintetizado como fallback quando não há MP3 carregado. */
function synthBeep(category, volume) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return false;
    const ctx = new AudioCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    // Frequências diferentes para severidades diferentes.
    const freq = {
      critical: 1200, major: 1000, minor: 880, warning: 720, info: 520,
      onu_offline: 620, power_fail: 300, los: 400, rx_low: 700,
      rx_critical: 1100, new_pending_onu: 900, olt_offline: 260,
    }[category] || 660;
    o.type = 'sine';
    o.frequency.value = freq;
    o.connect(g); g.connect(ctx.destination);
    const v = Math.max(0.02, Math.min(0.9, (volume ?? 0.7) * 0.4));
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(v, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    o.start();
    o.stop(ctx.currentTime + 0.55);
    return true;
  } catch (e) { return false; }
}

/**
 * Registra um callback que roda quando as configurações mudam (em qualquer aba).
 */
export function subscribeSettings(cb) {
  const handler = (e) => cb(e.detail || loadSettings());
  const storage = (e) => { if (e.key === STORAGE_KEY) cb(loadSettings()); };
  window.addEventListener('oltm-alarm-sounds-changed', handler);
  window.addEventListener('storage', storage);
  return () => {
    window.removeEventListener('oltm-alarm-sounds-changed', handler);
    window.removeEventListener('storage', storage);
  };
}
